import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Users, CalendarCheck, Building2, Wallet } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  mockDepartments,
  mockAttendance,
  mockPayrollMonths,
} from "@project/lib/mock-modules2";

export default function HrPayrollDashboard() {
  const headcount = mockDepartments.reduce((s, d) => s + d.headcount, 0);
  const todayAttendance = mockAttendance[mockAttendance.length - 1];
  const attendancePct = Math.round(
    (todayAttendance.present /
      (todayAttendance.present + todayAttendance.absent)) *
      100
  );
  const lastPayroll = mockPayrollMonths[mockPayrollMonths.length - 1];

  return (
    <AdminShell
      title="HR & Payroll"
      subtitle="Workforce, attendance & monthly payroll snapshot"
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Employees"
          value={headcount}
          icon={<Users className="h-5 w-5" />}
          accent="from-primary/90 to-primary"
          sub={`${mockDepartments.length} departments`}
        />
        <StatCard
          label="Attendance Today"
          value={`${attendancePct}%`}
          icon={<CalendarCheck className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub={`${todayAttendance.present} present · ${todayAttendance.absent} absent`}
        />
        <StatCard
          label="Departments"
          value={mockDepartments.length}
          icon={<Building2 className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub="Across the campus"
        />
        <StatCard
          label="Last Payroll Run"
          value={`₹${(lastPayroll.amount / 100000).toFixed(2)}L`}
          icon={<Wallet className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
          sub={`${lastPayroll.month} 2026`}
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Department Overview"
          subtitle="Headcount & leads"
          className="lg:col-span-2"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {mockDepartments.map(d => (
              <div
                key={d.name}
                className="bg-background/40 rounded-xl border border-border p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-foreground">
                    {d.name}
                  </div>
                  <div className="bg-primary/10 rounded-full px-2 py-0.5 text-xs font-semibold text-primary">
                    {d.headcount}
                  </div>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Lead · {d.lead}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Payroll Snapshot" subtitle="Last 6 months (₹ lakh)">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={mockPayrollMonths.map(m => ({
                  month: m.month,
                  lakh: +(m.amount / 100000).toFixed(2),
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar
                  dataKey="lakh"
                  fill="hsl(var(--primary))"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      <div className="mt-4">
        <SectionCard title="Attendance Summary" subtitle="This week">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mockAttendance}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="day" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar
                  dataKey="present"
                  stackId="a"
                  fill="hsl(var(--primary))"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="absent"
                  stackId="a"
                  fill="hsl(var(--muted-foreground))"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
